import React, { useMemo, useState } from "react";
import {
  FaBolt,
  FaBrain,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaFilePdf,
  FaInfoCircle,
  FaLightbulb,
  FaRedo,
  FaShieldAlt,
} from "react-icons/fa";
import AIInsightsService from "../services/AIInsightsService";
import { downloadAIInsightsPdf } from "../services/AIInsightsPdfService";
import { buildCompactAIInsightView } from "./AIInsightsPresentation";

const healthLabels = {
  good: "Cenário favorável",
  stable: "Cenário estável",
  attention: "Pontos de atenção",
  critical: "Ação prioritária",
};

const priorityClass = {
  high: "border-rose-200 bg-rose-50 text-rose-800",
  medium: "border-amber-200 bg-amber-50 text-amber-800",
  low: "border-sky-200 bg-sky-50 text-sky-800",
};

const ListCard = ({ label, values, icon: Icon }) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
    <h4 className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
      <Icon className="text-blue-600" /> {label}
    </h4>
    <ul className="mt-3 space-y-2">
      {(values || []).slice(0, 5).map((value, index) => (
        <li key={index} className="text-sm leading-relaxed text-slate-600">• {value}</li>
      ))}
      {(!values || values.length === 0) && (
        <li className="text-sm text-slate-400">Nenhum ponto relevante identificado.</li>
      )}
    </ul>
  </div>
);

export default function AIInsightsPanel({ module, title, subtitle, data, compact = false }) {
  const [report, setReport] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [error, setError] = useState("");
  const payload = useMemo(() => data || {}, [data]);
  const compactView = useMemo(() => buildCompactAIInsightView(report || {}), [report]);

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      setReport(await AIInsightsService.generate(module, payload, prompt));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Não foi possível gerar os insights.");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!report || pdfLoading) return;
    setPdfLoading(true);
    setError("");
    try {
      await downloadAIInsightsPdf({ report, title: title || "Relatório com insights de IA", module });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Não foi possível gerar o PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <section
      className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      <div className="bg-gradient-to-br from-[#06143f] via-[#092c78] to-[#0b63ce] p-6 text-white md:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-sky-100">
              <FaBrain /> Nash IA
            </span>
            <div className="mt-3 flex items-center gap-2">
              <h2 className="text-2xl font-extrabold">{title || "Relatório com insights de IA"}</h2>
              <span className="group relative inline-flex" tabIndex={0}>
                <FaInfoCircle className="cursor-help text-base text-blue-200 transition group-hover:text-white group-focus:text-white" />
                <span
                  role="tooltip"
                  className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-72 -translate-x-1/2 rounded-xl border border-white/15 bg-slate-950/95 px-3 py-2 text-xs font-medium leading-relaxed text-white shadow-xl group-hover:block group-focus:block"
                >
                  {subtitle || "Análise executiva aprofundada dos dados atuais, com evidências, riscos, oportunidades e próximos passos."}
                </span>
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {report && (
              <button
                type="button"
                onClick={downloadPdf}
                disabled={pdfLoading}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-white/20 disabled:opacity-60"
              >
                <FaFilePdf /> {pdfLoading ? "Gerando PDF..." : "Baixar PDF"}
              </button>
            )}
            <button
              type="button"
              onClick={generate}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-extrabold text-[#092c78] shadow-lg transition hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-70"
            >
              {loading ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#092c78] border-t-transparent" />
              ) : report ? <FaRedo /> : <FaBolt />}
              {loading ? "Analisando..." : report ? "Atualizar análise" : "Gerar insights"}
            </button>
          </div>
        </div>
        {!compact && (
          <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-3">
            <input
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="Opcional: peça um foco específico, comparação ou questão para a Nash IA investigar"
              className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white outline-none placeholder:text-blue-200 focus:border-white/40"
            />
          </div>
        )}
      </div>

      {error && (
        <div className="m-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700">
          {error}
        </div>
      )}

      {!report && !loading && !error && (
        <div className="p-6">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><FaLightbulb /></span>
            <div>
              <h3 className="text-sm font-extrabold text-slate-900">Resumo executivo com IA</h3>
              <p className="mt-1 text-sm text-slate-500">Gere poucos indicadores, prioridades e ações. A análise completa fica disponível apenas quando você quiser abrir os detalhes.</p>
            </div>
          </div>
        </div>
      )}

      {report && (
        <div className="space-y-5 p-6 md:p-8">
          <div className="flex flex-col gap-3 rounded-2xl border border-blue-100 bg-blue-50/50 p-5 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-blue-600">
                {healthLabels[report.health] || "Análise atual"}
              </p>
              <h3 className="mt-1 text-lg font-extrabold text-slate-950">{report.headline}</h3>
              <p className="mt-2 max-w-4xl text-sm leading-relaxed text-slate-600">{report.summary}</p>
            </div>
            <span className="whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-500 shadow-sm">
              {report.generatedAt ? new Date(report.generatedAt).toLocaleString("pt-BR") : "Agora"}
            </span>
          </div>

          {compactView.metrics.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {compactView.metrics.map((metric, index) => (
                <div key={String(metric.label) + "-" + index} className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">{metric.label}</p>
                  <p className="mt-1 text-xl font-black text-slate-950">{metric.value}</p>
                  {metric.context && <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{metric.context}</p>}
                </div>
              ))}
            </div>
          )}

          {compactView.priorities.length > 0 && (
            <div>
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-extrabold uppercase tracking-[0.12em] text-slate-500">Prioridades</h3>
                <span className="text-xs font-semibold text-slate-400">Até 3 pontos principais</span>
              </div>
              <div className="grid gap-3 lg:grid-cols-3">
                {compactView.priorities.map((item, index) => (
                  <article key={String(item.title) + "-" + index} className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-extrabold text-slate-900">{item.title}</h4>
                      <span className={"shrink-0 rounded-full border px-2 py-1 text-[10px] font-extrabold uppercase " + (priorityClass[item.priority] || priorityClass.low)}>
                        {item.priority === "high" ? "Alta" : item.priority === "medium" ? "Média" : "Baixa"}
                      </span>
                    </div>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">{item.description}</p>
                    {item.action && (
                      <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs font-semibold leading-relaxed text-slate-700">
                        <strong>Ação:</strong> {item.action}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            </div>
          )}

          {compactView.hasDetails && (
            <div className="border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setDetailsOpen((current) => !current)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-extrabold text-slate-600 transition hover:border-blue-200 hover:text-blue-700"
              >
                {detailsOpen ? <FaChevronUp /> : <FaChevronDown />}
                {detailsOpen ? "Ocultar análise detalhada" : "Ver análise detalhada"}
              </button>

              {detailsOpen && (
                <div className="mt-5 space-y-5">
                  {Array.isArray(report.diagnostics) && report.diagnostics.length > 0 && (
                    <div>
                      <h3 className="mb-3 text-sm font-extrabold uppercase tracking-[0.12em] text-slate-500">Diagnóstico</h3>
                      <div className="grid gap-3 lg:grid-cols-2">
                        {report.diagnostics.slice(0, 5).map((item, index) => (
                          <article key={String(item.dimension) + "-" + index} className="rounded-2xl border border-slate-200 p-4">
                            <p className="text-[11px] font-extrabold uppercase tracking-wide text-blue-600">{item.dimension}</p>
                            <h4 className="mt-1 text-sm font-extrabold text-slate-900">{item.finding}</h4>
                            <p className="mt-2 text-xs leading-relaxed text-slate-600">{item.evidence}</p>
                            <p className="mt-2 text-xs font-semibold leading-relaxed text-slate-700"><strong>Impacto:</strong> {item.impact}</p>
                          </article>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <ListCard label="Riscos" values={report.risks} icon={FaExclamationTriangle} />
                    <ListCard label="Oportunidades" values={report.opportunities} icon={FaLightbulb} />
                    <ListCard label="Tendências" values={report.forecastSignals} icon={FaBrain} />
                    <ListCard label="Qualidade dos dados" values={report.dataQuality} icon={FaShieldAlt} />
                    <ListCard label="Próximos passos" values={report.nextSteps} icon={FaCheckCircle} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
